import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { AdminOnly } from '@/components/admin-only'
import { Page, PageHeader, Card, CardHeader, btnPrimary, SkeletonRows, EmptyState, KpiCard } from '@/components/rm'
import { Field, Input, Select } from '@/components/rm/form'
import { useAuth } from '@/hooks/use-auth'
import { fmtDateTime, pesoFromCentavos } from '@/lib/ph'
import { usePayouts } from '@/lib/escrow'
import { errorText } from '@/lib/utils'

// Spec 008 US4. Where released bookings are paid out, and the account they go to. Owners and admins.
export const Route = createFileRoute('/_app/payouts')({ component: () => <AdminOnly><Payouts /></AdminOnly> })

const STATUS = { waiting_account: 'Waiting for your account', queued: 'Queued for sending', sent: 'Sent' } as const
const KIND = { gcash: 'GCash', maya: 'Maya', bank: 'Bank account' } as const

function Payouts() {
  const { companyId } = useAuth()
  const { q, saveAccount } = usePayouts()
  const [kind, setKind] = useState<'gcash' | 'maya' | 'bank'>('gcash')
  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [bank, setBank] = useState('')
  const d = q.data
  const sum = (k: string) => (d?.ledger ?? []).filter((l) => l.kind === k).reduce((n, l) => n + l.amount_centavos, 0)
  const paidOut = (d?.payouts ?? []).filter((p) => p.status === 'sent').reduce((n, p) => n + p.amount_centavos, 0)
  const waiting = (d?.payouts ?? []).filter((p) => p.status !== 'sent').reduce((n, p) => n + p.amount_centavos, 0)

  return (
    <Page>
      <PageHeader title="Payouts" subtitle="Money from completed MySerbisyo bookings, after the commission, the payment fee and the payout fee." />
      {q.isLoading && <Card className="mt-5"><SkeletonRows /></Card>}
      {d && (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <KpiCard label="Paid out" value={pesoFromCentavos(paidOut)} />
            <KpiCard label="Waiting to be sent" value={pesoFromCentavos(waiting)} />
            <KpiCard label="Commission to date" value={pesoFromCentavos(-sum('commission'))} />
          </div>

          <Card className="mt-5">
            <CardHeader title="Payout account" />
            <div className="p-5">
              {d.account && <p className="mb-4 text-[14px] text-token-primary">{KIND[d.account.kind]} · {d.account.account_name}{d.account.bank_name ? ` · ${d.account.bank_name}` : ''} · ending {d.account.number_last4}</p>}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Send payouts to"><Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                  <option value="gcash">GCash</option><option value="maya">Maya</option><option value="bank">Bank account (InstaPay or PesoNET)</option>
                </Select></Field>
                <Field label="Account name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Juan dela Cruz" /></Field>
                <Field label={kind === 'bank' ? 'Account number' : 'Mobile number'}><Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder={kind === 'bank' ? '0123 4567 8901' : '0917 123 4567'} inputMode="numeric" /></Field>
                {kind === 'bank' && <Field label="Bank"><Input value={bank} onChange={(e) => setBank(e.target.value)} placeholder="BDO, BPI, Metrobank" /></Field>}
              </div>
              <p className="mt-3 text-[12.5px] text-token-secondary">Only owners and admins of your business can see this. It is shown masked except the last 4 digits.</p>
              <div className="mt-4 flex justify-end">
                <button className={btnPrimary} disabled={saveAccount.isPending || !name.trim() || number.replace(/\D/g, '').length < 6 || (kind === 'bank' && !bank.trim())}
                  onClick={async () => {
                    try {
                      await saveAccount.mutateAsync({ company_id: companyId!, kind, account_name: name.trim(), account_number: number.trim(), bank_name: kind === 'bank' ? bank.trim() : null })
                      toast.success('Payout account saved'); setName(''); setNumber(''); setBank('')
                    } catch (e) { toast.error(errorText(e, 'Could not save the account')) }
                  }}>{d.account ? 'Replace account' : 'Save account'}</button>
              </div>
            </div>
          </Card>

          <Card className="mt-5">
            <CardHeader title="Payouts" />
            {d.payouts.length === 0
              ? <EmptyState label="No payouts yet. A booking is paid out when the client confirms the job, or 72 hours after you mark it done." />
              : <div className="divide-y divide-token-subtle">{d.payouts.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                    <div>
                      <p className="text-[14px] font-semibold text-token-primary">{pesoFromCentavos(p.amount_centavos)}</p>
                      <p className="text-[12.5px] text-token-secondary">{STATUS[p.status]} · {fmtDateTime(p.sent_at ?? p.created_at)}</p>
                    </div>
                    <Link to="/mkt-bookings" className="text-[13px] font-semibold text-brand hover:underline">Booking</Link>
                  </div>
                ))}</div>}
          </Card>
        </>
      )}
    </Page>
  )
}
